<script setup lang="ts" generic="TSchema extends Object">
import {
  computed,
  ref,
} from 'vue';

import type { VFormFilterButtonProps } from '@modules/form-widgets/components/VFormFilterButton/types';
import type {
  FormResult,
  FormSchema,
  VFormRef,
} from '@modules/forms/components/VForm/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import VFormButtonsGroupWidget from '@modules/form-widgets/components/VFormButtonsGroupWidget/VFormButtonsGroupWidget.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import {
  flattenSchema,
  isKeyOfSchema,
} from '@modules/forms/utils';
import {
  assert,
  isKeyOf,
  isNumber,
  isString,
} from '@utils/guards';

import type { VFormButtonsWithFormGroupWidgetEmits, VFormButtonsWithFormGroupWidgetProps } from './types';
import {
  isStepItemGuard,

} from './types';

const props = withDefaults(defineProps<VFormButtonsWithFormGroupWidgetProps<TSchema>>(), {});
const emit = defineEmits<VFormButtonsWithFormGroupWidgetEmits<TSchema>>();

const addKey = 'add';
const form = ref<VFormRef>();

const selectedButton = ref<number | string>();
const tempValue = ref<TSchema>();

// eslint-disable-next-line sonarjs/cognitive-complexity
function createLabelFromSchema(
  formSchema: FormSchema<TSchema>,
  formValues?: TSchema,
  valueSuffix = '',
): string {
  if (!formValues) {
    return '';
  }

  const label: string[] = [];
  const flattenedSchema = flattenSchema(formSchema);
  const values = isStepItemGuard(formValues)
    ? {
        obtained_result: formValues.obtained_result,
        condition_for_comparison: formValues.condition_for_comparison,
        desired_value: formValues.desired_value,
      }
    : formValues;

  for (const [key, value] of Object.entries(values)) {
    const prefixKey = `${key}`;
    let optionLabel;

    if (!isKeyOfSchema(flattenedSchema, prefixKey)) {
      continue;
    }

    const schemaElement = flattenedSchema[prefixKey];

    switch (schemaElement?.type) {
      case EFormInputTypes.SELECT:
      case EFormInputTypes.RADIO:
        optionLabel = schemaElement.options.find((item) => item.value === value)?.label;

        if (optionLabel) {
          label.push(optionLabel);
        }
        break;
      default: {
        if (isString(value)) {
          label.push(value);
          break;
        }

        if (isNumber(value)) {
          label.push(String(value));
          break;
        }
      }
    }
  }

  if (label.length > 0 && valueSuffix) {
    label.splice(0, 1, `${label[0]} ${valueSuffix}`);
  }

  return label.join(' ').trim();
}

const formButtons = computed<VFormFilterButtonProps[]>(() => (
  props.buttons.map(({
    formSchema,
    value,
    valueSuffix,
    ...item
  }, index) => {
    const currentValue = index === selectedButton.value && tempValue.value ? tempValue.value : value;

    return {
      ...item,
      label: createLabelFromSchema(formSchema, currentValue, valueSuffix) || item.label,
      isClickable: item.isClickable ?? true,
    } as VFormFilterButtonProps;
  })
));

function closeDialog(): void {
  selectedButton.value = undefined;
  tempValue.value = undefined;
}

function onSelectItem(index: number): void {
  selectedButton.value = index;
}

const preparedSchema = computed<FormSchema<TSchema> | undefined>(() => {
  if (isNumber(selectedButton.value)) {
    const button = props.buttons[selectedButton.value];

    if (button) {
      return Object.entries(button.formSchema).reduce((accumulator, [key, value]) => {
        assert(isKeyOf(button.formSchema, key));

        Object.assign(accumulator, {
          [key]: {
            ...value,
            value: button.value?.[key] ?? value.value,
          },
        });

        return accumulator;
      }, {} as FormSchema<TSchema>);
    }
  }

  if (selectedButton.value === addKey && props.addButton) {
    return props.addButton.formSchema;
  }

  return undefined;
});

function onSubmitBtnClick(): void {
  form.value?.submit();
}

function onInteracted(key: unknown, value: unknown, { values }: FormResult<TSchema>): void {
  tempValue.value = values;

  if (selectedButton.value !== undefined) {
    emit('input', selectedButton.value, values);
  }
}

function emitSubmit(values: TSchema): void {
  if (selectedButton.value === addKey) {
    emit('add', values);
    return;
  }

  if (isNumber(selectedButton.value)) {
    emit('change', selectedButton.value, values);
  }
}

function onSubmit(values: TSchema): void {
  emitSubmit(values);
  closeDialog();
}

function onAdd(): void {
  selectedButton.value = addKey;
}

function onRemove(index: number): void {
  emit('remove', index);
}
</script>

<template>
  <div v-data-qa-auto-id="'VFormButtonsWithFormGroupWidget'">
    <VFormButtonsGroupWidget
      :label="label"
      :buttons="formButtons"
      :add-label="addButton?.label"
      :add-type="addButton?.type"
      :errors="errors"
      :readonly="readonly"
      :flex-direction-column="flexDirectionColumn"
      @select-item="onSelectItem"
      @add="onAdd"
      @remove="onRemove"
    />

    <VDialog
      :title="formTitle"
      :visible="!!preparedSchema"
      @hide="closeDialog"
    >
      <template #default>
        <VForm
          v-if="preparedSchema"
          ref="form"
          :schema="preparedSchema"
          :readonly="readonly"
          @interacted="onInteracted"
          @submit="onSubmit"
        />
      </template>
      <template #footer>
        <VButton
          v-if="formCloseBtn"
          :label="formCloseBtn"
          outlined
          @click="closeDialog"
        />
        <VButton
          v-if="formSubmitBtn && !readonly"
          :label="formSubmitBtn"
          @click="onSubmitBtnClick"
        />
      </template>
    </VDialog>
  </div>
</template>
